import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { getField } from './fieldSaga';
import { getHarvestSeasons } from './harvestSaga';
import { getMosaicData } from './mosaicSaga';
import { getPrecPlantingSeasons } from './precisionPlantingSaga';
import { getProductAssets } from './productAssetsSaga';
import { getFieldPermission } from './sharingSaga';
import { getCropList } from './cropSaga';
import {
  REQUEST_APP_INITIALIZER,
  RECEIVE_CURRENT_USER_DETAILS,
} from '../constants/actionTypes';
import {
  MICROSOFT_WEATHER_GDD,
  FEATURE_LIST,
  DMB_CROP_SEASON_FILTER,
} from '../constants/featureFlags';
import { reqSoilSsurgoZones } from '../actions/mosaicActions';

import { appInitializerFailure } from '../actions/appInitializerActions';
import {
  receiveSelectorVals,
  fieldNotFoundFailure,
} from '../actions/selectorActions';
import { receiveUserDetails, reqUserDetails } from '../actions/userActions';
import { receivePlantingAnalytics } from '../actions/plantingActions';
import { reqCurrentUserDetails } from '../actions/currentUserActions';

import { extractSeasons, extractMosaicSeasons } from '../utils/seasonData';
import { initialize as initializeFeatureFlags } from '../utils/featureFlags';
import { extractMapLayers } from '../utils/layerData';
import { reqActivities } from '../actions/scoutingActions';
import { reqObservations } from '../actions/weatherActions';
import {
  getActiveSelectorVals,
  determineRequestedSelectorIds,
  updateSelectorIdsInStorage,
} from '../utils/selectorOptions';
import { logDmbError } from '../utils/error';
import { getFieldsMultiOperation } from './fieldsMultiOperSaga';
import { receiveFeatures, reqFeatures } from '../actions/featureActions';
import { requestPlantingEvents } from '../requesters/plantingRequester/olPlantingRequester';
import { requestUapiPlantingEvents } from '../requesters/plantingRequester/uapiPlantingRequester';
import { CropAction } from '../actions/cropActions';
import { getPlantingArdDataFeatureSelector } from '../selectors/featureSelectors';
import { FeatureFlagKey } from '../types';
import { getSeasonTagWithWildcards } from '../utils/seasonUtils';

export function* getUserData() {
  const { currentUser, user } = yield select();
  const currentUserId = currentUser.clientId;

  yield put(reqCurrentUserDetails(currentUserId));
  const { userDetails: currentUserData } = yield take(
    RECEIVE_CURRENT_USER_DETAILS
  );
  const { keys, values } = yield call(
    initializeFeatureFlags,
    currentUserData.id,
    currentUserData.countryCode
  );
  yield put(receiveFeatures(keys, values));

  if (user.isProxying) {
    yield put(reqUserDetails(user.id, true));
  } else {
    yield put(reqFeatures(FEATURE_LIST));
  }
}

/**
 * Calls the getField saga and getHarvestSeasons saga synchronously
 * @param {String} fieldId - either Field Legacy Id or Field UUID
 * @return {void}
 */
export function* fieldDeps(fieldId) {
  // Get crop list for harvest calculations
  yield fork(getCropList);

  // Get field info used for the following API calls
  const { fieldUUID, fieldLegacyId, fieldOwnerId } = yield call(getField, fieldId);

  const state = yield select();
  const currentUserId = state.currentUser.clientId;
  const { isProxying } = state.user;
  const isPlantingArdDataEnabled = getPlantingArdDataFeatureSelector(state);

  if (!isProxying) {
    if (currentUserId === fieldOwnerId) {
      yield put(receiveUserDetails(state.currentUser, false));
    } else {
      yield put(reqUserDetails(fieldOwnerId));
    }
  }

  let plantingCall;
  if (isPlantingArdDataEnabled) {
    const cropsMap = yield take(CropAction.ReceiveCropList);
    plantingCall = call(requestUapiPlantingEvents, fieldUUID, cropsMap.cropList);
  } else {
    plantingCall = call(requestPlantingEvents, fieldUUID);
  }

  const callSteps = [
    call(getProductAssets, fieldUUID, isProxying ? fieldOwnerId : undefined),
    call(getMosaicData, fieldLegacyId, fieldOwnerId),
    call(getPrecPlantingSeasons, fieldUUID),
    call(getHarvestSeasons, fieldUUID, fieldOwnerId),
    plantingCall
  ];

  const [
    productAssets,
    mosaicData,
    precPlantingSeasons,
    harvestSeasons,
    plantingEvents,
  ] = yield all(callSteps);

  return {
    harvestSeasons,
    mosaicData,
    precPlantingSeasons,
    productAssets,
    plantingEvents,
  };
}

/**
 * Waits for the return of the REQUEST_SEASONS action event and
 * dispatches the receiveSelectorVals action
 * @param {Object} action
 * @return {void}
 */
export function* getAppInitializer(action) {
  const { fields, currentUser } = yield select((store) => store);
  const { fieldId, mapLayerIds, seasonIds } = action;

  const fieldsIds = fields?.allIds;
  const { hasFetched } = currentUser;

  let cropMap;
  let fieldPermission;
  let harvestSeasons;
  let mosaicData;
  let precPlantingSeasons;
  let plantingSeasons;
  let productAssets;
  let plantingEvents;

  try {
    if (!hasFetched) {
      // on first go call user data, next ones (field id changes), dont re-request user data
      yield call(getUserData);
    }

    [
      { cropList: cropMap },
      {
        harvestSeasons,
        mosaicData,
        plantingSeasons,
        precPlantingSeasons,
        productAssets,
        plantingEvents,
      },
      fieldPermission,
    ] = yield all([
      take(CropAction.ReceiveCropList),
      call(fieldDeps, fieldId),
      call(getFieldPermission, fieldId),
    ]);
    if (fieldsIds?.length === 0) yield call(getFieldsMultiOperation);
  } catch (error) {
    logDmbError(error, {
      appLocation: 'app initializer saga',
    });
    yield put(appInitializerFailure());
    return;
  }

  const {
    feature: { userConfigFeatures: featureFlags },
    field,
  } = yield select();

  const { seasonList, liquidSeasons } = precPlantingSeasons;
  const isPlantingArdDataEnabled = Boolean(featureFlags[FeatureFlagKey.WEB_PLANTING_ARD_DATA]?.value);


  if (
    harvestSeasons ||
    plantingSeasons ||
    seasonList ||
    plantingEvents?.plantingSeasons
  ) {
    const harvestSeas = harvestSeasons || [];
    const plantingSeas = plantingSeasons || [];
    const precPlantingSeas = seasonList || [];
    const plantingAnalyticsSeasons = plantingEvents?.plantingSeasons || [];
    const mosaicSeasons = extractMosaicSeasons(mosaicData.seasonSpecific);
    const selectorIds = { fieldId, mapLayerIds, seasonIds };

    let seasonsArr = !isPlantingArdDataEnabled ?  [
      ...harvestSeas,
      ...plantingSeas,
      ...precPlantingSeas,
      ...mosaicSeasons,
      ...plantingAnalyticsSeasons,
    ] : [
      ...harvestSeas,
      ...precPlantingSeas,
      ...mosaicSeasons,
    ];

    const isYearSeasonOnly = ({season}) => season.split(':').length === 1;

    const fullSeasonsArray = seasonsArr.filter(season => !isYearSeasonOnly(season));
    const hasSeasonWithSameYear = ({season}, lookupYear) => season.split(':')[0] === lookupYear;
    const includeYearOnlyIfNoFullVersion = (season) => {
      if (isYearSeasonOnly(season)) {
        return !fullSeasonsArray.find(fullSeason => hasSeasonWithSameYear(fullSeason, season.season));
      }
      
      return true;
    }

    seasonsArr = seasonsArr.filter(includeYearOnlyIfNoFullVersion);

    if (isPlantingArdDataEnabled) {
      const ardPlantingSeasons = plantingAnalyticsSeasons.filter(
        ({season: plantingSeasonTag}) => {
          const exists = seasonsArr.find(({season: legacySeasonCode}) => getSeasonTagWithWildcards(legacySeasonCode) === plantingSeasonTag);
          return !exists;
      });

      seasonsArr = [
        ...seasonsArr,
        ...ardPlantingSeasons,
      ]
    }
    
    yield put(receivePlantingAnalytics(plantingEvents.events ?? {}));

    // Extract Seasons and Map Layers
    // @TODO: Add Prescription Seasons
    const { seasons, sortedSeasonKeys } = extractSeasons(
      seasonsArr,
      liquidSeasons,
      cropMap,
      { cropSeasonFilterEnabled: Boolean(featureFlags[DMB_CROP_SEASON_FILTER]?.value) }
    );

    const layers = extractMapLayers(
      seasons,
      sortedSeasonKeys,
      fieldPermission,
      mosaicData,
      cropMap,
      productAssets,
      featureFlags
    );

    // Determine active selector vals, based on requested vals; update sessionStorage
    const requestedIds = determineRequestedSelectorIds(selectorIds);
    const activeVals = getActiveSelectorVals(
      seasons,
      sortedSeasonKeys,
      layers,
      requestedIds
    );
    const { activeMapLayers, activeSeasons } = activeVals;

    const activeSeason = activeSeasons[0].id;
    const activeMapLayer = activeMapLayers[0].id;

    updateSelectorIdsInStorage(
      selectorIds.fieldId,
      activeMapLayer,
      activeSeason
    );

    // Dispatch receive action - this will be picked up by the seasonReducer and layersReducer
    yield put(
      receiveSelectorVals(seasons, sortedSeasonKeys, layers, activeVals)
    );
    if (mosaicData.seasonAgnostic.soilssurgo) {
      yield put(
        reqSoilSsurgoZones(mosaicData.seasonAgnostic.soilssurgo.imageId)
      );
    }

    const useMicrosoftGDD = featureFlags[MICROSOFT_WEATHER_GDD].value;

    yield put(reqActivities(field.uuid));
    yield put(reqObservations(field.id, activeSeason, useMicrosoftGDD));
  } else {
    logDmbError(new Error('Field not found'), {
      mapLayerIds,
      seasonIds,
    });
    yield put(fieldNotFoundFailure());
  }
}

/**
 * Waits for the return of the REQUEST_SEASONS action event and calls the getAppInitializer saga
 * @return {void}
 */
export function* getAppInitializerWatcher() {
  yield takeLatest(REQUEST_APP_INITIALIZER, getAppInitializer);
}

/**
 * Uses fork to start multiple watchers in parallel
 * @return {void}
 */
export default function* rootSaga() {
  yield fork(getAppInitializerWatcher);
}
